import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { mediaApi, type MediaFile } from "@/lib/api.js";
import { useAuthStore } from "@/store/auth.js";

interface Props {
  label: string;
  value: string;
  onChange: (url: string) => void;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPreviewable(url: string) {
  return !url.match(/\.(ico|svg)(\?|$)/i);
}

function FilePreview({ url, name, className }: { url: string; name?: string; className?: string }) {
  const [failed, setFailed] = useState(false);
  const ext = (url.split('.').pop()?.split('?')[0] ?? '').toLowerCase();

  if (!isPreviewable(url) || failed) {
    return (
      <div className={`flex flex-col items-center justify-center bg-gray-50 ${className ?? ''}`}>
        <span className="text-2xl">🖼</span>
        <span className="mt-1 text-[10px] font-mono text-gray-400 uppercase">.{ext || '?'}</span>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={name ?? ''}
      className={`object-cover ${className ?? ''}`}
      onError={() => setFailed(true)}
    />
  );
}

export function MediaPickerField({ label, value, onChange }: Props) {
  const { activeTenantId } = useAuthStore();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["media", activeTenantId],
    queryFn: () => mediaApi.list(),
    enabled: open,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => mediaApi.upload(file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["media", activeTenantId] });
      onChange(res.url);
      setOpen(false);
    },
    onError: (err) => alert(err instanceof Error ? err.message : "업로드에 실패했습니다."),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => mediaApi.delete(key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["media", activeTenantId] }),
    onError: (err) => alert(err instanceof Error ? err.message : "삭제에 실패했습니다."),
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    e.target.value = "";
  }

  const files: MediaFile[] = data?.files ?? [];

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-500">{label}</label>
      <div className="flex items-center gap-3">
        {value ? (
          <div className="h-10 w-10 rounded-lg border border-gray-200 overflow-hidden bg-gray-50 shrink-0">
            <FilePreview url={value} className="h-full w-full" />
          </div>
        ) : (
          <div className="h-10 w-10 rounded-lg border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center text-gray-300 text-lg">+</div>
        )}
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="URL을 직접 입력하거나 파일을 선택하세요"
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
        />
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="shrink-0 rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          파일 선택
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setOpen(false)}>
          <div className="relative w-full max-w-2xl mx-4 rounded-2xl bg-white shadow-xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <h3 className="text-sm font-bold text-gray-800">미디어 선택</h3>
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" accept="image/*,.ico,.svg" className="hidden" onChange={handleFileChange} />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadMutation.isPending}
                  className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {uploadMutation.isPending ? "업로드 중..." : "+ 업로드"}
                </button>
                <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-2 py-1.5 text-sm text-gray-400 hover:bg-gray-100">✕</button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12 text-sm text-gray-400">불러오는 중...</div>
              ) : files.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <p className="text-sm">업로드된 파일이 없습니다</p>
                  <p className="mt-1 text-xs">위 버튼으로 파일을 업로드하세요</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3">
                  {files.map((f) => (
                    <div key={f.key} className={`group relative rounded-xl border-2 overflow-hidden cursor-pointer transition-all ${value === f.url ? "border-brand-500" : "border-transparent hover:border-gray-300"}`}>
                      <div className="aspect-square bg-gray-100 overflow-hidden" onClick={() => { onChange(f.url); setOpen(false); }}>
                        <FilePreview url={f.url} name={f.name} className="h-full w-full" />
                      </div>
                      <div className="px-1.5 py-1">
                        <p className="truncate text-[10px] text-gray-500">{f.name}</p>
                        <p className="text-[10px] text-gray-400">{formatBytes(f.size)}</p>
                      </div>
                      {value === f.url && (
                        <div className="absolute top-1.5 right-1.5 h-5 w-5 rounded-full bg-brand-500 flex items-center justify-center">
                          <span className="text-white text-[10px]">✓</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (confirm("삭제할까요?")) deleteMutation.mutate(f.key); }}
                        className="absolute bottom-8 right-1 hidden group-hover:flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-[10px]"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
